import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, ChartLineUp } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function NotesGenerales() {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/grades/overview").then((r) => setStudents(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const courses = useMemo(() => {
    const names = new Set();
    students.forEach((s) => s.grades.forEach((g) => g.course && names.add(g.course)));
    return [...names].sort();
  }, [students]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      if (courseFilter !== "all" && !s.grades.some((g) => g.course === courseFilter)) return false;
      if (q && !(`${s.student_name} ${s.email} ${s.username || ""}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [students, search, courseFilter]);

  const downloadPdf = async (s) => {
    try {
      const res = await api.get(`/users/${s.student_id}/grades/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = `releve-${s.student_name}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="notes-generales-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Évaluations</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Notes des étudiants</h1>
        <p className="text-sm text-muted-foreground">Vue d'ensemble des notes obtenues par vos étudiants dans vos cours.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un étudiant..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="notes-search" />
        </div>
        {courses.length > 0 && (
          <Select value={courseFilter} onValueChange={setCourseFilter}>
            <SelectTrigger className="w-52" data-testid="notes-course-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les cours</SelectItem>
              {courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={ChartLineUp}
          title="Aucun étudiant à afficher"
          description={students.length === 0 ? "Aucun étudiant n'a accès à vos cours pour l'instant, ou aucune note n'a encore été attribuée." : "Aucun résultat pour cette recherche."}
        />
      ) : (
        <>
          {/* Vue carte (mobile) */}
          <div className="space-y-3 md:hidden">
            {filtered.map((s) => (
              <button key={s.student_id} onClick={() => setDetail(s)} className="w-full rounded-xl border border-border bg-card p-4 text-left" data-testid={`notes-card-${s.student_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{s.student_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{s.filiere || "—"} {s.promotion ? `· ${s.promotion}` : ""}</p>
                  </div>
                  <span className="shrink-0 font-display text-lg font-bold text-primary">{s.average != null ? `${s.average}/20` : "—"}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{s.grades.length} note(s)</p>
              </button>
            ))}
          </div>

          {/* Vue tableau (desktop) */}
          <div className="hidden rounded-xl border border-border bg-card md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Étudiant</TableHead>
                  <TableHead>Filière</TableHead>
                  <TableHead>Promotion</TableHead>
                  <TableHead className="text-right">Moyenne</TableHead>
                  <TableHead className="text-right">Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.student_id} data-testid={`notes-row-${s.student_id}`}>
                    <TableCell className="font-medium">{s.student_name}</TableCell>
                    <TableCell className="text-muted-foreground">{s.filiere || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{s.promotion || "—"}</TableCell>
                    <TableCell className="text-right font-display font-bold text-primary">{s.average != null ? `${s.average}/20` : "—"}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{s.grades.length}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setDetail(s)} data-testid={`notes-detail-${s.student_id}`}>Détail</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.student_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-secondary p-4">
                <div className="text-sm text-muted-foreground">
                  {detail.filiere || "—"} {detail.promotion ? `· ${detail.promotion}` : ""} · {detail.email}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-display text-2xl font-bold text-primary">{detail.average != null ? `${detail.average}/20` : "—"}</span>
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(detail)} data-testid="notes-detail-export"><DownloadSimple size={14} className="mr-2" /> PDF</Button>
                </div>
              </div>
              {detail.grades.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune note enregistrée.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Intitulé</TableHead>
                      <TableHead>Cours</TableHead>
                      <TableHead className="text-right">Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.grades.map((g, i) => (
                      <TableRow key={i}>
                        <TableCell><Badge variant="secondary">{g.type}</Badge></TableCell>
                        <TableCell className="font-medium">{g.title}</TableCell>
                        <TableCell className="text-muted-foreground">{g.course || "—"}</TableCell>
                        <TableCell className="text-right font-display font-bold">{g.grade}/{g.max}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
